import { data, type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'
import type { Player } from '@/playback/api'
import { globalRef, noop, type Unsubscribe } from '@amazon/vinyl-util'
import { globalData } from '@/util/globalData'

/**
 * Sleep-timer presets. Minute presets tick a countdown; end-of-track
 * subscribes to the next `trackEnded` event and pauses. Mirrors GH's set
 * (5/15/30/45/60 minutes + end-of-track).
 */
export type SleepTimerPreset =
    | 'endOfTrack'
    | 'minutes5'
    | 'minutes15'
    | 'minutes30'
    | 'minutes45'
    | 'minutes60'

const PRESET_MINUTES: Record<Exclude<SleepTimerPreset, 'endOfTrack'>, number> = {
    minutes5: 5,
    minutes15: 15,
    minutes30: 30,
    minutes45: 45,
    minutes60: 60,
}

// Internal mutable references — the module owns writes; the exported
// `ObservableValue` views below make writing from outside a type error.
const _remaining: MutableValue<number | null> = globalData(() => data<number | null>(null))
const _active: MutableValue<boolean> = globalData(() => data(false))
const _preset: MutableValue<SleepTimerPreset | null> = globalData(() =>
    data<SleepTimerPreset | null>(null)
)

/**
 * Remaining seconds on the active minute-based timer. `null` when no
 * countdown is running (i.e. inactive, or end-of-track waiting). UI
 * renders a `mm:ss` label when non-null.
 */
export const sleepTimerRemaining$: ObservableValue<number | null> = _remaining

/**
 * True while any sleep timer is armed (either counting down or waiting
 * for the next `trackEnded` event).
 */
export const sleepTimerActive$: ObservableValue<boolean> = _active

/**
 * Which preset is currently armed, or `null` when inactive. Lets the UI
 * mark the active row in the preset menu.
 */
export const sleepTimerPreset$: ObservableValue<SleepTimerPreset | null> = _preset

let playerPromise: Promise<Player> | null = null

interface TimerHandles {
    intervalId: ReturnType<typeof setInterval> | null
    endedUnsub: Unsubscribe | null
    // Bumped by every cancel/start so a deferred `playerPromise.then()` from a
    // superseded call can tell it's stale — comparing against `_preset.value`
    // isn't enough, since a later call re-arming the SAME preset would make an
    // earlier, already-superseded callback's check pass too.
    generation: number
    dispose(): void
}

// Disposed on a registry reset so an armed countdown can't keep ticking.
const timerRef = globalRef<TimerHandles>(() => ({
    intervalId: null,
    endedUnsub: null,
    generation: 0,
    dispose() {
        clearHandles(this)
    },
}))

/**
 * Wire the controller to the app's player. Called once from `app.ts`
 * during dependency setup. Subsequent starts read the player through
 * this promise so the module has no circular import with playback.
 */
export function initializeSleepTimer(deps: { readonly playerPromise: Promise<Player> }): void {
    playerPromise = deps.playerPromise
}

function clearHandles(handles: TimerHandles): void {
    if (handles.intervalId !== null) {
        clearInterval(handles.intervalId)
        handles.intervalId = null
    }
    if (handles.endedUnsub) {
        handles.endedUnsub()
        handles.endedUnsub = null
    }
}

function clearActiveTimer(): void {
    clearHandles(timerRef.value)
}

/**
 * Cancel any armed timer without pausing. Resets observables to the
 * inactive state.
 */
export function cancelSleepTimer(): void {
    timerRef.value.generation++
    clearActiveTimer()
    _remaining.value = null
    _active.value = false
    _preset.value = null
}

function pausePlayer(): void {
    if (!playerPromise) return
    playerPromise
        .then((p) => {
            p.pause()
        })
        .catch(noop)
}

/**
 * Arms the timer for `preset`. Any previously-armed timer is canceled
 * first — the presets are one-of, not additive.
 *
 * Minute presets tick every second and pause playback at zero. The
 * end-of-track preset subscribes to the next `trackEnded` event once, then
 * pauses (so the currently-playing track finishes but the next one
 * doesn't auto-play).
 */
export function startSleepTimer(preset: SleepTimerPreset): void {
    cancelSleepTimer()
    _active.value = true
    _preset.value = preset

    if (preset === 'endOfTrack') {
        if (!playerPromise) return
        const myGeneration = timerRef.value.generation
        playerPromise
            .then((player) => {
                // The controller may have been canceled or superseded while
                // awaiting the player — bail so we don't attach a stale listener.
                if (myGeneration !== timerRef.value.generation) return
                const finish = () => {
                    player.pause()
                    // Reset observables directly — clearActiveTimer
                    // would try to call `off()` again.
                    timerRef.value.endedUnsub = null
                    _active.value = false
                    _preset.value = null
                    _remaining.value = null
                }
                // The track already ended before this promise settled, so no `trackEnded` is coming.
                if (player.ended$.value) {
                    finish()
                    return
                }
                timerRef.value.endedUnsub = player.on('trackEnded', finish, { once: true })
            })
            .catch(noop)
        return
    }

    _remaining.value = PRESET_MINUTES[preset] * 60
    timerRef.value.intervalId = setInterval(() => {
        const remaining = (_remaining.value ?? 0) - 1
        if (remaining <= 0) {
            clearActiveTimer()
            _remaining.value = null
            _active.value = false
            _preset.value = null
            pausePlayer()
            return
        }
        _remaining.value = remaining
    }, 1000)
}
